import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeStore } from '@/stores';
import { useAuthStore } from '@/stores';
import { useNotificationStore } from '@/stores';
import { useLogout } from '@/hooks/useAuth';
import {
  Sun,
  Moon,
  RefreshCw,
  Wifi,
  WifiOff,
  ChevronLeft,
  ChevronRight,
  Globe,
  LogOut,
  Menu,
  X,
  Bell,
} from 'lucide-react';

const pageNameMap: Record<string, { ar: string; en: string }> = {
  '/': { ar: 'لوحة التحكم', en: 'Dashboard' },
  '/customers': { ar: 'العملاء', en: 'Customers' },
  '/subscriptions': { ar: 'الاشتراكات', en: 'Subscriptions' },
  '/requests': { ar: 'الطلبات', en: 'Requests' },
  '/tasks': { ar: 'المهام', en: 'Tasks' },
  '/wallet': { ar: 'المحفظة', en: 'Wallet' },
  '/locations': { ar: 'المناطق', en: 'Locations' },
  '/notifications': { ar: 'مركز التنبيهات', en: 'Notification Center' },
  '/alerts': { ar: 'إدارة التنبيهات والقنوات', en: 'Alerts Management' },
  '/settings': { ar: 'الإعدادات', en: 'Settings' },
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, language, setTheme, setLanguage, toggleSidebar, sidebarOpen } = useThemeStore();
  const { user, isAuthenticated } = useAuthStore();
  const { unreadCount } = useNotificationStore();
  const { logout } = useLogout();

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isRTL = language === 'ar';
  const isLight = theme === 'light';
  const pageName = pageNameMap[location.pathname];
  const currentPageName = pageName ? (language === 'ar' ? pageName.ar : pageName.en) : '';

  const toggleLanguage = () => {
    const newLang = language === 'ar' ? 'en' : 'ar';
    setLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const userInitial = user?.username?.charAt(0)?.toUpperCase() || 'P';
  const userName = user?.username || 'مقاول تجريبي';

  return (
    <header
      style={{
        background: isLight
          ? '#ffffff'
          : 'linear-gradient(90deg, #0d1f3c 0%, #0a1628 100%)',
        borderBottom: isLight
          ? '1px solid #e2e8f0'
          : '1px solid rgba(255,255,255,0.08)',
        boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 16px',
        direction: isRTL ? 'rtl' : 'ltr',
        gap: isMobile ? '8px' : '12px',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Toggle Sidebar button - Hamburger menu on mobile */}
      <button
        onClick={toggleSidebar}
        style={{
          background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.12)',
          borderRadius: '8px',
          padding: isMobile ? '8px 10px' : '6px 8px',
          color: isLight ? '#475569' : '#94a3b8',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: isMobile ? '36px' : 'auto',
          minHeight: isMobile ? '36px' : 'auto',
        }}
      >
        {isMobile ? (
          sidebarOpen ? <X size={18} /> : <Menu size={18} />
        ) : (
          isRTL ? (
            sidebarOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />
          ) : (
            sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />
          )
        )}
      </button>

      {/* Current page name */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'nowrap',
        }}
      >
        <span style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontWeight: 700, fontSize: isMobile ? '14px' : '16px', whiteSpace: 'nowrap' }}>
          {currentPageName}
        </span>
      </div>

      {/* Spacer to push controls to the opposite end */}
      <div style={{ flex: 1 }} />

      {/* Controls Group: Status Badge, Theme Toggle, Language Toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '6px' : '10px',
          background: isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.03)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '9999px',
          padding: '4px 8px',
          flexShrink: 0,
        }}
      >
        {/* Network Status badge (Circular) */}
        {isAuthenticated ? (
          <div
            className="header-status-badge"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(16,185,129,0.15)',
              border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: '50%',
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              padding: '0',
              color: '#10b981',
              flexShrink: 0,
              cursor: 'pointer',
            }}
            title="الشبكة الوطنية (متصل)"
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#10b981',
                animation: 'pulse 2s infinite',
              }}
            />
          </div>
        ) : (
          <div
            className="header-status-badge"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(180,83,9,0.15)',
              border: '1px solid rgba(251,191,36,0.3)',
              borderRadius: '50%',
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              padding: '0',
              color: '#fbbf24',
              flexShrink: 0,
              cursor: 'pointer',
            }}
            title="غير متصل"
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#fbbf24',
              }}
            />
          </div>
        )}

        {/* Vertical divider inside group */}
        <div style={{ width: '1px', height: '16px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)' }} />

        {/* Theme toggle */}
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          style={{
            background: 'transparent',
            border: 'none',
            color: isLight ? '#475569' : '#94a3b8',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: isMobile ? '28px' : '32px',
            height: isMobile ? '28px' : '32px',
            borderRadius: '50%',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
          title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        >
          {theme === 'dark' ? <Sun size={isMobile ? 18 : 16} /> : <Moon size={isMobile ? 18 : 16} />}
        </button>

        {/* Vertical divider inside group */}
        {/* Vertical divider inside group */}
        <div style={{ width: '1px', height: '16px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)' }} />

        {/* Notification Bell */}
        <button
          onClick={() => navigate('/notifications')}
          style={{
            background: location.pathname === '/notifications'
              ? (isLight ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.25)')
              : 'transparent',
            border: 'none',
            color: unreadCount > 0 ? '#3b82f6' : (isLight ? '#475569' : '#94a3b8'),
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            flexShrink: 0,
            width: isMobile ? '28px' : '32px',
            height: isMobile ? '28px' : '32px',
            borderRadius: '50%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = location.pathname === '/notifications'
              ? (isLight ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.25)')
              : 'transparent';
          }}
          title="مركز التنبيهات"
        >
          <Bell size={isMobile ? 17 : 16} />
          {unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                background: '#ef4444',
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: 700,
                minWidth: '16px',
                height: '16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 3px',
                border: '1.5px solid ' + (isLight ? '#ffffff' : '#0d1f3c'),
                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.4)',
                animation: 'pulse 2s infinite',
              }}
            >
              {unreadCount > 99 ? '+99' : unreadCount}
            </span>
          )}
        </button>

        {/* Vertical divider inside group */}
        <div style={{ width: '1px', height: '16px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)' }} />

        {/* Language */}
        <button
          onClick={toggleLanguage}
          style={{
            background: 'transparent',
            border: 'none',
            color: isLight ? '#475569' : '#94a3b8',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: 600,
            flexShrink: 0,
            borderRadius: '8px',
            transition: 'background 0.2s',
            height: isMobile ? '28px' : '32px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Globe size={isMobile ? 16 : 14} />
          <span style={{ fontSize: isMobile ? '11px' : '12px' }}>{language === 'ar' ? 'EN' : 'ع'}</span>
        </button>
      </div>

      {/* Divider */}
      {!isMobile && (
        <div className="header-divider" style={{ width: '1px', height: '24px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.12)' }} />
      )}

      {/* Refresh */}
      {!isMobile && (
        <button
          className="header-refresh-btn"
          style={{
            background: 'transparent',
            border: 'none',
            color: isLight ? '#475569' : '#94a3b8',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      )}

      {/* User avatar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
        }}
      >
        {!isMobile && (
          <span className="header-username" style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '13px', fontWeight: 500 }}>
            {userName}
          </span>
        )}
        <div
          style={{
            width: isMobile ? '28px' : '32px',
            height: isMobile ? '28px' : '32px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: isMobile ? '12px' : '14px',
            flexShrink: 0,
          }}
        >
          {userInitial}
        </div>
      </div>

      {/* Logout button */}
      <button
        onClick={logout}
        className="header-logout-btn"
        style={{
          background: 'rgba(239,68,68,0.12)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px',
          padding: isMobile ? '6px' : '6px 12px',
          color: '#f87171',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          fontSize: '12px',
          fontWeight: 600,
          transition: 'all 0.2s',
          flexShrink: 0,
          width: isMobile ? '28px' : 'auto',
          height: isMobile ? '28px' : 'auto',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(239,68,68,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
        }}
        title="خروج"
      >
        <LogOut size={isMobile ? 14 : 13} />
        {!isMobile && <span className="header-logout-text">خروج</span>}
      </button>
    </header>
  );
};

export default Header;
